import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Share2 } from "lucide-react";
import { api } from "@/lib/api";

export const buyDownload = async (item, format) => {
  try {
    const image_url = item.image_url.startsWith("/") ? window.location.origin + item.image_url : item.image_url;
    const { data } = await api.post("/downloads/checkout", {
      title: item.title,
      image_url,
      format,
      artwork_id: item.id,
      origin_url: window.location.origin,
    });
    window.location.href = data.checkout_url;
  } catch {
    toast.error("Errore nell'avvio del pagamento. Riprova.");
  }
};

export const buyOriginal = async (art) => {
  try {
    const { data } = await api.post("/artworks/checkout", { artwork_id: art.id, origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  } catch {
    toast.error("Errore nell'avvio del pagamento. Riprova.");
  }
};
import ArtworkCard from "@/components/ArtworkCard";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const buyBundle = async (format) => {
  try {
    const { data } = await api.post("/downloads/bundle-checkout", { format, origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  } catch {
    toast.error("Errore nell'avvio del pagamento. Riprova.");
  }
};

export default function Galleria() {
  const [artworks, setArtworks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(false);
  const [params] = useSearchParams();

  const shareUrl = (art) => `${window.location.origin}/api/share/opera/${art.id}`;
  const shareText = (art) =>
    `"${art.title}" di Italo Blandolino — collezione REBIRTH and CONTEMPORANEITY su Wildersquad 🎨`;

  const shareWhatsApp = (art) => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText(art)}\n${shareUrl(art)}`)}`, "_blank", "noopener");
  };

  const shareInstagram = async (art) => {
    if (navigator.share) {
      try {
        await navigator.share({ title: art.title, text: shareText(art), url: shareUrl(art) });
        return;
      } catch {
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(`${shareText(art)}\n${shareUrl(art)}`);
      toast.success("Link copiato! Incollalo nella tua storia o bio Instagram 📸");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setZoom(false);
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [zoom]);
  useEffect(() => {
    api.get("/artworks").then((r) => {
      setArtworks(r.data);
      const openId = params.get("opera");
      if (openId) {
        const art = r.data.find((a) => a.id === openId);
        if (art) setSelected(art);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="galleria-page">
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-4">Galleria</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">Italo Blandolino</h1>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
            <p className="text-sm text-[#C9C4B8] leading-relaxed">
              Italo Blandolino è un fioraio con la passione per l'arte figurativa, un osservatore sensibile della
              fragilità e della bellezza umana. Attraverso la pittura ad olio rielabora temi classici e spirituali
              con un linguaggio contemporaneo, dove ironia e introspezione convivono in perfetto equilibrio.
              <em> "Sacro e profano viaggiano insieme ma non si toccano mai."</em>
            </p>
            <p className="text-sm text-[#C9C4B8] leading-relaxed">
              La collezione <em>REBIRTH and CONTEMPORANEITY</em>, pensata durante il periodo pandemico e realizzata
              tra il 2022 e il 2023, è disponibile in originale, riproduzione numerata (fisica e AI) e NFT su Crypto.com.
              Contatti: <a className="ws-link text-[#E4BD44]" href="tel:+393284432930">+39 328 443 2930</a> ·
              IG <a className="ws-link text-[#E4BD44]" href="https://www.instagram.com/bedliw" target="_blank" rel="noopener noreferrer">@bedliw</a> ·
              <a className="ws-link text-[#E4BD44]" href="mailto:bewildwildbe@gmail.com"> bewildwildbe@gmail.com</a>
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-[#2A2A2A] bg-[#141414]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="galleria-artist-bio">
          <div className="lg:col-span-4">
            <div className="art-frame aspect-square border border-[#2A2A2A]">
              <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/mcuvrh33_photo_2025-10-22%2000.11.51.jpeg" alt="Italo Blandolino tra bottega e atelier" />
            </div>
            <p className="text-xs text-[#9a9484] mt-3 font-mono-ws">Italo Blandolino — tra fiori, tele e statue sacre</p>
          </div>
          <div className="lg:col-span-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-3">L'artista</p>
            <h2 className="font-display text-lg font-bold mb-4">Profilo di Italo Blandolino</h2>
            <p className="text-sm text-[#C9C4B8] leading-relaxed mb-4">
              Italo Blandolino è un fioraio con la passione per l'arte figurativa, un osservatore sensibile della
              fragilità e della bellezza umana. Attraverso la pittura ad olio rielabora temi classici e spirituali
              con un linguaggio contemporaneo, dove ironia e introspezione convivono in perfetto equilibrio.
              La sua arte è un dialogo costante tra il quotidiano e l'eterno, tra la memoria collettiva e
              l'esperienza personale, in un percorso che unisce l'eleganza del passato alla forza emotiva del presente.
            </p>
            <p className="font-display text-xl italic text-[#C9C4B8] mb-4">
              "Sacro e profano viaggiano insieme ma non si toccano mai."
            </p>
            <p className="text-sm text-[#C9C4B8]">
              La collezione, pensata durante il periodo pandemico e realizzata tra il 2022 e il 2023, tende a
              sensibilizzare e far tornare indietro nel tempo di qualche anno, proiettandoti verso il futuro.
            </p>
          </div>
        </div>
      </section>

      <section className="dark-section bg-[#121212] text-[#F9F6F0] border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-testid="bundle-banner">
          <div className="lg:col-span-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Per i collezionisti</p>
            <h2 className="font-display text-lg font-bold mb-2">Pacchetto Collezionista — tutta la collezione in digitale</h2>
            <p className="text-sm text-[#b8b2a4] leading-relaxed max-w-2xl">
              Le 5 opere di REBIRTH and CONTEMPORANEITY in alta qualità, con certificato di copyright su ogni file:
              <strong className="text-[#D4AF37]"> €29 invece di €45</strong>. Scegli il formato: un unico PDF sfogliabile o le singole immagini JPG (ZIP).
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap lg:justify-end gap-3">
            <button onClick={() => buyBundle("pdf")} className="btn-primary" data-testid="bundle-pdf-button">
              PDF unico · €29
            </button>
            <button onClick={() => buyBundle("zip")} className="btn-outline !border-[#D4AF37] !text-[#F9F6F0]" data-testid="bundle-zip-button">
              JPG (ZIP) · €29
            </button>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {artworks.map((a) => (
              <ArtworkCard key={a.id} art={a} onClick={() => setSelected(a)} />
            ))}
          </div>
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          onInteractOutside={(e) => { if (zoom) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (zoom) e.preventDefault(); }}
          className="max-w-3xl bg-[#0A0A0A] border-[#2A2A2A] rounded-none p-0 max-h-[90vh] overflow-y-auto"
        >
          {selected && (
            <div className="grid grid-cols-1 md:grid-cols-2" data-testid="artwork-detail-modal">
              <DialogTitle className="sr-only">{selected.title}</DialogTitle>
              <DialogDescription className="sr-only">Scheda opera di Italo Blandolino</DialogDescription>
              <div className="art-frame aspect-[4/5] cursor-zoom-in relative group" onClick={() => setZoom(true)} data-testid="artwork-modal-image">
                <img src={selected.image_url} alt={selected.title} />
                <span className="absolute bottom-2 right-2 bg-[#121212]/70 text-[#F9F6F0] text-[10px] uppercase tracking-[0.15em] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Clicca per ingrandire
                </span>
              </div>
              <div className="p-7">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E4BD44] mb-3">{selected.collection}</p>
                <h2 className="font-display text-3xl font-bold mb-2">{selected.title}</h2>
                <p className="text-xs text-[#9a9484] mb-5">
                  {selected.technique} · {selected.size} · {selected.year}
                  {selected.edition ? ` · edizione ${selected.edition}` : ""}
                </p>
                <p className="text-sm text-[#C9C4B8] leading-relaxed mb-6">{selected.description}</p>
                <p className="font-display text-3xl font-bold mb-4">
                  {selected.price ? `€${selected.price.toLocaleString("it-IT")}` : "Prezzo su richiesta"}
                </p>
                {selected.nft && (
                  <p className="text-xs text-[#D4AF37] border border-[#D4AF37] inline-block px-3 py-1 mb-4 uppercase tracking-[0.15em]">
                    Disponibile come NFT su {selected.nft_platform}
                  </p>
                )}
                {selected.editions_left != null && (
                  <div className="border border-[#2A2A2A] bg-[#141414] px-4 py-3 mb-6" data-testid="editions-certificate">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#E4BD44] mb-1">Riproduzioni numerate</p>
                    <p className="text-sm">
                      <strong className="font-display text-lg">{selected.editions_left}</strong> di 10 ancora disponibili
                      <span className="text-xs text-[#9a9484]"> · fisiche, AI e NFT, stesso prezzo</span>
                    </p>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mb-5">
                  {selected.price && selected.available !== false ? (
                    <button onClick={() => buyOriginal(selected)} className="btn-primary" data-testid="buy-original-button">
                      Acquista l'originale · €{selected.price.toLocaleString("it-IT")}
                    </button>
                  ) : selected.price ? (
                    <span className="text-xs uppercase tracking-[0.2em] border border-[#9a9484] text-[#9a9484] px-3 py-2" data-testid="artwork-sold-badge">
                      Originale venduto
                    </span>
                  ) : null}
                  <a href="mailto:bewildwildbe@gmail.com?subject=Richiesta opera" className="btn-outline" data-testid="artwork-inquiry-button">
                    Richiedi info
                  </a>
                </div>
                <div className="flex items-center gap-2 mb-5" data-testid="artwork-share-bar">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#9a9484] mr-1">Condividi</span>
                  <button onClick={() => shareWhatsApp(selected)} data-testid="share-whatsapp-button"
                    title="Condividi su WhatsApp"
                    className="w-9 h-9 border border-[#2A2A2A] flex items-center justify-center text-[#C9C4B8] hover:border-[#25D366] hover:text-[#25D366] transition-colors">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </button>
                  <button onClick={() => shareInstagram(selected)} data-testid="share-instagram-button"
                    title="Condividi su Instagram"
                    className="w-9 h-9 border border-[#2A2A2A] flex items-center justify-center text-[#C9C4B8] hover:border-[#E4405F] hover:text-[#E4405F] transition-colors">
                    <Instagram size={16} />
                  </button>
                  <button onClick={() => shareInstagram(selected)} data-testid="share-generic-button"
                    title="Altre opzioni di condivisione"
                    className="w-9 h-9 border border-[#2A2A2A] flex items-center justify-center text-[#C9C4B8] hover:border-[#E4BD44] hover:text-[#E4BD44] transition-colors">
                    <Share2 size={15} />
                  </button>
                </div>
                <div className="border-t border-[#2A2A2A] pt-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E4BD44] mb-2">Copia digitale in alta qualità — €9</p>
                  <div className="flex gap-2 mb-3">
                    <button onClick={() => buyDownload(selected, "jpg")} className="btn-outline !py-2 !px-4 text-xs" data-testid="download-jpg-button">
                      Scarica JPG · €9
                    </button>
                    <button onClick={() => buyDownload(selected, "pdf")} className="btn-outline !py-2 !px-4 text-xs" data-testid="download-pdf-button">
                      Scarica PDF · €9
                    </button>
                  </div>
                  <p className="text-[10px] text-[#9a9484] leading-relaxed" data-testid="copyright-clause">
                    © Tutti i diritti riservati. L'acquisto concede una licenza d'uso personale e non commerciale.
                    È vietato il riutilizzo, la riproduzione e la ridistribuzione — anche parziale — senza licenza
                    e royalty concordate con Wildersquad.
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {zoom && selected && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center cursor-zoom-out p-4"
          style={{ pointerEvents: "auto" }}
          onClick={() => setZoom(false)}
          data-testid="artwork-lightbox"
        >
          <button
            className="absolute top-5 right-5 text-[#F9F6F0] border border-[#F9F6F0]/40 hover:border-[#E4BD44] hover:text-[#E4BD44] w-11 h-11 flex items-center justify-center text-xl transition-colors"
            onClick={(e) => { e.stopPropagation(); setZoom(false); }}
            aria-label="Chiudi ingrandimento"
            data-testid="lightbox-close-button"
          >
            ✕
          </button>
          <img src={selected.image_url} alt={selected.title} className="max-h-full max-w-full object-contain" />
          <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[#F9F6F0]/70 text-xs uppercase tracking-[0.2em]">
            {selected.title} · clicca per chiudere
          </p>
        </div>
      )}
    </div>
  );
}
